import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import Footer from '../components/Footer';
import CategoryPills from '../components/CategoryPills';
import ProductCard from '../components/ProductCard';
import ShopEventCard from '../components/ShopEventCard';
import { useLocation } from '../contexts/LocationContext';
import { useT } from '../i18n/I18nContext';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const { location: userLocation } = useLocation();
  const { t } = useT();
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const isEventsMode = selectedCategory === 'eventi';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedCategory === 'eventi') {
        const params = new URLSearchParams();
        if (userLocation) {
          params.append('lat', userLocation.lat);
          params.append('lng', userLocation.lng);
        }
        params.append('days', '30');
        params.append('limit', '50');
        const res = await axios.get(`${API}/events/upcoming?${params.toString()}`);
        setEvents(res.data);
        setProducts([]);
        return;
      }
      const params = new URLSearchParams();
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      if (selectedCategory) {
        params.append('gruppo', selectedCategory);
      }
      // Pill "servizi" includes appointments where availability is N/A.
      if (selectedCategory !== 'servizi') {
        params.append('disponibile', 'true');
      }
      params.append('limit', '30');

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
      setEvents([]);
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
      setLoading(false);
    }
  }, [userLocation, selectedCategory]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (query) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="home-page">
      <Header onSearch={handleSearch} />

      <main className="px-4 py-4 max-w-7xl mx-auto">
        {/* Categories */}
        <section className="mb-6">
          <CategoryPills
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
          />
        </section>

        {/* Products Section */}
        <section className="pb-8">
          <h2 className="font-outfit font-semibold text-lg text-slate-800 mb-4" data-testid="section-title">
            {selectedCategory
              ? t('home.products_category_title', {
                  category: t(`categories.${selectedCategory}`) || (selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)),
                })
              : t('home.products_title')}
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : isEventsMode ? (
            events.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
                {events.map((ev) => (
                  <ShopEventCard key={ev.id} event={ev} />
                ))}
              </div>
            ) : (
              <div className="empty-state bg-white rounded-2xl">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-4xl">📅</span>
                </div>
                <h3 className="font-outfit font-semibold text-slate-700 mb-2">
                  {t('events.empty_title')}
                </h3>
                <p className="text-slate-500 text-sm">{t('events.empty_subtitle')}</p>
              </div>
            )
          ) : products.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state bg-white rounded-2xl">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-4xl">🔍</span>
              </div>
              <h3 className="font-outfit font-semibold text-slate-700 mb-2">
                {t('home.empty_title')}
              </h3>
              <p className="text-slate-500 text-sm">
                {userLocation
                  ? t('home.empty_with_location')
                  : t('home.empty_no_location')}
              </p>
            </div>
          )}
        </section>
      </main>

      <Footer />
      <BottomNav />
    </div>
  );
}
